// Greenskin, bandit and monster creature prefabs: orc, ogre, goblin, sorcerer,
// dragon, bandit, warlord, orc wolf rider. Copied from adventure-craft.
import { box, humanoid } from './creatures_shared.js';
import type { CharacterPrefab } from './types.js';

// ─── Greenskins ──────────────────────────────────────────────
export const orc: CharacterPrefab = {
  name: 'Orc',
  maxHp: 45,
  faction: 'greenskin',
  parts: humanoid('#2d5a1e', '#3d6a2e', '#2d4a1e', '#3d6a2e', '#ff4444', 4, 6, 5, true),
};

export const ogre: CharacterPrefab = {
  name: 'Ogre',
  maxHp: 80,
  faction: 'greenskin',
  behavior: 'heavy',
  parts: [
    {
      name: 'head',
      voxels: [
        ...box(-3, 16, -2, 6, 5, 5, '#6b8e5a'),
        { x: -2, y: 19, z: 3, color: '#ff4444' },
        { x: 2, y: 19, z: 3, color: '#ff4444' },
      ],
    },
    {
      name: 'body',
      voxels: [...box(-4, 7, -2, 8, 9, 5, '#5a7a4a'), ...box(-3, 9, -3, 6, 5, 1, '#4a6a3a')],
    },
    { name: 'left_arm', voxels: box(-7, 9, 0, 3, 8, 3, '#6b8e5a') },
    {
      name: 'right_arm',
      voxels: [
        ...box(4, 9, 0, 3, 8, 3, '#6b8e5a'),
        ...box(6, 7, -1, 2, 12, 2, '#5c3317'),
        { x: 7, y: 18, z: 0, color: '#8b4513' },
      ],
    },
    { name: 'left_leg', voxels: box(-3, 0, 0, 3, 7, 3, '#4a5a3a') },
    { name: 'right_leg', voxels: box(0, 0, 0, 3, 7, 3, '#4a5a3a') },
  ],
};

export const goblin: CharacterPrefab = {
  name: 'Goblin',
  maxHp: 15,
  faction: 'greenskin',
  behavior: 'swarm',
  parts: humanoid('#4a7a2e', '#5a8a3e', '#3a5a1e', '#5a8a3e', '#ffcc00', 3, 4, 4),
};

// ─── Demons / Casters ────────────────────────────────────────
export const sorcerer: CharacterPrefab = {
  name: 'Sorcerer',
  maxHp: 50,
  faction: 'demon',
  parts: humanoid('#2a1a3a', '#3a2a4a', '#2a1a3a', '#3a2a4a', '#ff00ff', 4, 6, 5),
};

// ─── Dragon ──────────────────────────────────────────────────
export const dragon: CharacterPrefab = {
  name: 'Dragon',
  maxHp: 200,
  faction: 'boss',
  canFly: true,
  isBoss: true,
  skeletonType: 'quadruped',
  parts: [
    {
      name: 'head',
      voxels: [
        ...box(-2, 14, 4, 4, 3, 6, '#8b0000'),
        ...box(-1, 14, 10, 2, 2, 2, '#5a0000'),
        ...box(-3, 14, -2, 6, 5, 6, '#a00000'),
        { x: -2, y: 17, z: 3, color: '#ffd700' },
        { x: 2, y: 17, z: 3, color: '#ffd700' },
        ...box(-4, 18, -1, 2, 4, 2, '#3a3a3a'),
        ...box(3, 18, -1, 2, 4, 2, '#3a3a3a'),
        ...box(-1, 19, -2, 2, 3, 4, '#5a0000'),
      ],
    },
    {
      name: 'body',
      voxels: [
        ...box(-4, 6, -4, 8, 8, 8, '#8b0000'),
        ...box(-3, 6, -3, 6, 6, 1, '#d4a574'),
        { x: 0, y: 14, z: -4, color: '#5a0000' },
        { x: 0, y: 13, z: -5, color: '#5a0000' },
        { x: 0, y: 12, z: -6, color: '#5a0000' },
      ],
    },
    {
      name: 'left_wing',
      voxels: [...box(-8, 10, -2, 4, 6, 8, '#700000'), ...box(-12, 10, 0, 4, 4, 6, '#600000')],
    },
    {
      name: 'right_wing',
      voxels: [...box(4, 10, -2, 4, 6, 8, '#700000'), ...box(8, 10, 0, 4, 4, 6, '#600000')],
    },
    {
      name: 'tail',
      voxels: [
        ...box(-2, 8, -8, 4, 4, 4, '#8b0000'),
        ...box(-1, 8, -12, 2, 3, 4, '#7a0000'),
        ...box(-1, 8, -16, 2, 2, 4, '#6a0000'),
        { x: 0, y: 9, z: -17, color: '#3a3a3a' },
      ],
    },
    {
      name: 'left_leg',
      voxels: [...box(-5, 0, -2, 3, 6, 4, '#700000'), ...box(-6, 0, 1, 1, 1, 2, '#3a3a3a')],
    },
    {
      name: 'right_leg',
      voxels: [...box(2, 0, -2, 3, 6, 4, '#700000'), ...box(5, 0, 1, 1, 1, 2, '#3a3a3a')],
    },
  ],
};

// ─── Bandits ─────────────────────────────────────────────────
export const bandit: CharacterPrefab = {
  name: 'Bandit',
  maxHp: 25,
  faction: 'bandit',
  parts: humanoid('#c4956a', '#5a4a3a', '#3a2a1a', '#c4956a', '#1a1a1a', 4, 6, 5),
};

export const warlord: CharacterPrefab = {
  name: 'Warlord',
  maxHp: 120,
  faction: 'bandit',
  isBoss: true,
  parts: [
    {
      name: 'head',
      voxels: [
        ...box(-3, 16, -2, 6, 5, 5, '#c4956a'),
        ...box(-3, 21, -2, 6, 2, 6, '#ffd700'),
        { x: -2, y: 19, z: 3, color: '#4444ff' },
        { x: 2, y: 19, z: 3, color: '#4444ff' },
      ],
    },
    {
      name: 'body',
      voxels: [...box(-4, 6, -1, 8, 10, 4, '#4a3a2a'), ...box(-5, 8, 0, 1, 4, 2, '#6a6a6a')],
    },
    { name: 'left_arm', voxels: box(-6, 8, 0, 2, 8, 2, '#c4956a') },
    {
      name: 'right_arm',
      voxels: [
        ...box(4, 8, 0, 2, 8, 2, '#c4956a'),
        ...box(5, 6, -1, 1, 12, 1, '#888888'),
        { x: 5, y: 18, z: -1, color: '#ffd700' },
      ],
    },
    { name: 'left_leg', voxels: box(-3, 0, 0, 3, 6, 3, '#3a2a1a') },
    { name: 'right_leg', voxels: box(0, 0, 0, 3, 6, 3, '#3a2a1a') },
  ],
};

export const orcWolfRider: CharacterPrefab = {
  name: 'Orc Wolf Rider',
  maxHp: 65,
  faction: 'greenskin',
  skeletonType: 'quadruped',
  parts: [
    {
      name: 'head',
      voxels: [
        ...box(0, 8, 8, 4, 3, 4, '#555555'),
        { x: 1, y: 9, z: 12, color: '#ff4444' },
        { x: 3, y: 9, z: 12, color: '#ff4444' },
        ...box(1, 10, 5, 2, 1, 4, '#444444'),
      ],
    },
    {
      name: 'body',
      voxels: [...box(-1, 6, 0, 6, 4, 10, '#555555'), ...box(0, 8, 2, 4, 3, 4, '#3d6a2e')],
    },
    { name: 'left_arm', voxels: box(4, 9, 3, 1, 2, 2, '#3d6a2e') },
    {
      name: 'right_arm',
      voxels: [...box(4, 9, 5, 1, 2, 2, '#3d6a2e'), ...box(5, 8, 5, 1, 4, 1, '#8b4513')],
    },
    { name: 'front_left_leg', voxels: box(1, 0, 2, 2, 6, 2, '#444444') },
    { name: 'front_right_leg', voxels: box(1, 0, 8, 2, 6, 2, '#444444') },
    { name: 'back_left_leg', voxels: box(1, 0, 0, 2, 6, 2, '#444444') },
    { name: 'back_right_leg', voxels: box(3, 0, 6, 2, 6, 2, '#444444') },
    {
      name: 'tail',
      voxels: [...box(2, 8, -4, 2, 1, 4, '#444444'), ...box(2, 7, -6, 1, 1, 2, '#444444')],
    },
  ],
};
